import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { onAuthChange } from '../api/auth';
import { sessionGen } from '../session';
import { Icon, type IconName } from './Icon';

/**
 * 공용 토스트 (디자인 리뷰 2·3회차 M6: 관리자 저장·재분석의 성공/실패 피드백 없음).
 *
 * 전역 상태 라이브러리 없이 window 이벤트로 전달한다. 어느 화면에서든
 * `toast('저장되었습니다')` / `toast('실패: …', 'error')` 로 띄운다.
 * 되돌릴 수 있는 일(일괄 정지 · 역할 변경, 계획서 U11)은 `{ action: { label: '되돌리기', icon: 'undo', run } }` —
 * 버튼이 있는 토스트는 읽고 누를 시간을 주려고 10초 둔다. 같은 일을 다른 곳에서 마쳤으면 `dropToast(id)` 로 내린다.
 */
export type ToastTone = 'ok' | 'error' | 'info';
export interface ToastAction { label: string; icon?: IconName; run: () => void }
/**
 * sid — 토스트가 속한 세션 세대(session.ts). 로그아웃 · 다른 계정 · 같은 계정 재로그인이면 그 세션의 토스트와 행동은 내린다
 * (11회차 m56 · Codex 3차 N12). 12회차 m56 · Codex 4차 N17: 늦게 끝난 작업의 토스트는 **작업을 시작한 세션**으로 만든다 —
 * 예전에는 만든 때(로그아웃 뒤 · 새 로그인 뒤)의 세션이 찍혀 새 세션의 토스트로 떴다.
 */
interface ToastMsg { id: number; text: string; tone: ToastTone; action?: ToastAction; ms?: number; sid: number }

const EVT = 'aitex:toast';
const DROP = 'aitex:toast-drop';
let seq = 0;

/** 지금 세션 세대 — 토큰 재발급으로는 바뀌지 않고 로그아웃 · 로그인마다 바뀐다 */
export function toastSession(): number {
  return sessionGen();
}

/**
 * @param opt.sid 이 토스트가 속한 세션 — 비동기 작업은 시작할 때 잡은 값(startJob().sid)을 넘긴다. 지금 세션과 다르면 띄우지 않는다.
 */
export function toast(text: string, tone: ToastTone = 'ok', opt: { action?: ToastAction; ms?: number; sid?: number } = {}): number {
  const id = ++seq;
  const sid = opt.sid ?? toastSession();
  if (sid !== toastSession()) return id;   // 앞 세션의 늦은 알림 — 새 세션 화면에 띄우지 않는다
  window.dispatchEvent(new CustomEvent<ToastMsg>(EVT, { detail: { id, text, tone, action: opt.action, ms: opt.ms, sid } }));
  return id;
}
export function dropToast(id: number): void {
  window.dispatchEvent(new CustomEvent<number>(DROP, { detail: id }));
}

/**
 * 토스트가 비켜 설 행동 줄 (16회차 m68).
 *
 * 크기가 정해진 아래 고정 줄(휴대폰 아래 탭 막대 · 휴대폰 사진 검색 실행 막대 · 담은 원단 버튼과 패널)은 CSS(--float-b · on-runbar · on-tray)가
 * 그 위 --s3 에 토스트를 세운다. 화면마다 높이 · 자리가 바뀌는 줄 — 편집 화면의 저장 줄(바꾼 것이 있으면 화면 아래에 붙는다) ·
 * 태블릿 · PC 사진 검색 실행 줄(흐름 안) — 은 여기 등록하고, 토스트 묶음은 그 줄과 겹치면 줄 윗변 위 --s3 까지 올라간다.
 * 예전에는 저장 직후 3.5초 동안 성공 토스트가 375 저장 줄의 「바뀌는 것 확인」 을 덮어, 그 사이 누르면 토스트가 눌렸다.
 * 돌려준 ref 를 그 줄에 붙인다(줄이 늦게 그려지거나 사라져도 — 불러오는 중 · 휴지통 — 붙고 떨어질 때 등록 · 해제).
 * dep — 줄의 자리가 바뀌는 상태(편집 화면: 바뀐 칸이 생겨 화면 아래에 붙음). 크기 변화는 ResizeObserver, 굴림 · 창 크기는 토스터가 듣는다.
 */
const avoid = new Set<HTMLElement>();
type Box = { top: number; bottom: number; left: number; right: number; height?: number };
/**
 * 올리기 전 토스트 자리(t)를 몇 px 올려야 어느 줄(bars)과도 겹치지 않는가 — 줄 윗변 위 gap 까지.
 * 옮긴 자리를 모든 줄과 다시 잰다(Codex 10차 ②: 처음 자리와만 비교하면 아래 줄을 피해 올린 자리가 위 줄과 겹칠 수 있었다).
 * Codex 11차 새 2: 올림에 위 끝이 없어 줄이 화면 위까지 이어지면 토스트가 화면 밖으로 나갔다 — 토스트 윗변이 minTop(화면 위 끝 + gap)
 * 아래에 남는 자리에서만 고른다. 빈자리 후보는 제자리와 「각 줄 윗변 위 gap 에 토스트 아랫변」 뿐이다(빈자리의 가장 낮은 자리) —
 * 덜 올리는 것부터 재 처음 빈 곳. 화면 안에 빈자리가 없으면 화면 안에서 줄과 가장 덜 겹치는 자리(같으면 덜 올린 자리)
 */
function toastLift(t: Box, bars: Box[], gap: number, minTop = -Infinity): number {
  const xs = bars.filter((b) => (b.height ?? b.bottom - b.top) > 0 && b.right > t.left && b.left < t.right);
  if (!xs.length) return 0;
  const most = Math.max(0, Math.floor(t.top - minTop));   // 이만큼 올리면 토스트 윗변이 minTop
  // 줄마다 up(이만큼 이상 올리면 줄 윗변 위 gap) · down(이만큼 이하면 줄 아래) — 빈자리 후보와 겹침 판정이 같은 값을 쓴다.
  // 예전 판정식(b.top < t.bottom - L + gap)은 1/3px 좌표에서 후보 ceil(up) 을 반올림 차로 「겹침」 이라 해 빈자리를 놓쳤다(Codex 12차 새 2 정리)
  const lim = xs.map((b) => ({ up: t.bottom - b.top + gap, down: t.top - b.bottom }));
  const hits = (L: number) => lim.some((x) => L < x.up && L > x.down);
  const ups = [0, ...lim.map((x) => Math.ceil(x.up))].filter((L) => L >= 0 && L <= most).sort((a, b) => a - b);
  for (const L of ups) if (!hits(L)) return L;
  // 대체 배치 — 겹치는 높이의 합이 가장 작은 자리. 후보는 줄 경계에 토스트 윗변 · 아랫변을 맞춘 자리와 양 끝(겹침은 그 사이에서 선형)
  // 이동은 정수 px — 소수 경계(소수 좌표)면 양옆 정수 floor · ceil 을 둘 다 잰다(Codex 12차 새 2: 올림만 보면 0.25px 덜 겹치는 자리를 놓쳤다).
  // 「같은 겹침」 은 부동소수 반올림 차(1e-13 수준)까지 같게 본다 — 1/3px 좌표에서 같은 겹침이 끝자리만 달라 더 올린 자리를 고르지 않게
  const over = (L: number) => xs.reduce((s, b) => s + Math.max(0, Math.min(b.bottom, t.bottom - L) - Math.max(b.top, t.top - L)), 0);
  const cands = [0, most, ...xs.flatMap((b) => [t.bottom - b.top + gap, t.bottom - b.top, t.top - b.bottom])]
    .flatMap((L) => [Math.floor(L), Math.ceil(L)])
    .map((L) => Math.min(most, Math.max(0, L)));
  const os = cands.map(over);
  const least = Math.min(...os);
  return Math.min(...cands.filter((_, i) => os[i] <= least + 1e-6));
}
/**
 * 화면보다 높은 묶음(Codex 12차 기존 P3) — CSS 가 묶음 높이를 화면 위 끝 + --s3 까지로 막는다(.toaster max-height).
 * 그보다 길 때만 묶음 안에서 굴린다(data-scroll): 늘 굴림을 켜 두면 토스트 그림자(--shadow-lg)가 묶음 가장자리에서 잘린다.
 * 넘침이 1px 이하(소수 높이 반올림)면 켜지 않는다. 막 넘치기 시작하면(새 토스트 · 창이 낮아짐) 가장 새 토스트(맨 아래)부터 보인다
 */
function fitToaster(el: HTMLElement): boolean {
  const over = el.scrollHeight > el.clientHeight + 1;
  if (over !== el.hasAttribute('data-scroll')) {
    el.toggleAttribute('data-scroll', over);
    if (over) el.scrollTop = el.scrollHeight;
  }
  return over;
}
const AVOID_EVT = 'aitex:toast-avoid';
const bumpAvoid = () => window.dispatchEvent(new Event(AVOID_EVT));
export function useToastAvoid<T extends HTMLElement>(dep?: unknown): (el: T | null) => void {
  const [el, setEl] = useState<T | null>(null);
  useLayoutEffect(() => {
    if (!el) return;
    avoid.add(el);
    bumpAvoid();
    const ro = new ResizeObserver(bumpAvoid);
    ro.observe(el);
    return () => { ro.disconnect(); avoid.delete(el); bumpAvoid(); };
  }, [el]);
  useLayoutEffect(() => { if (el) bumpAvoid(); }, [el, dep]);
  return setEl;
}

/**
 * 알림 영역은 #root 밖(body 포털) · 창 배경보다 위 — 창이 열려 #root 가 inert 여도 보이고 낭독된다(10회차 m47 · Codex N2).
 * 담은 원단 버튼 · 사진 검색 실행 막대 위로 올리는 여백은 앱 틀이 알려 준다(tray · runbar). 편집 저장 줄처럼 자리가 바뀌는 줄은 useToastAvoid(16회차 m68).
 * 11회차 m54: 담은 원단 패널이 열려 있으면 패널을 덮지 않는 자리로 옮긴다(CSS :root.tray-open) — 휴대폰에서는 패널 위,
 * 패널은 토스트 높이(--toast-h)만큼 비켜 준다.
 */
export default function Toaster({ tray, runbar }: { tray?: boolean; runbar?: boolean }) {
  const [items, setItems] = useState<ToastMsg[]>([]);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const on = (e: Event) => {
      const m = (e as CustomEvent<ToastMsg>).detail;
      if (m.sid !== toastSession()) return;
      setItems((xs) => [...xs, m]);
      // 오류는 조금 더 오래 보여 준다 — 읽기 전에 사라지면 피드백이 없는 것과 같다
      window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== m.id)),
                        m.ms ?? (m.action ? 10000 : m.tone === 'error' ? 7000 : 3500));
    };
    const off = (e: Event) => { const id = (e as CustomEvent<number>).detail; setItems((xs) => xs.filter((x) => x.id !== id)); };
    window.addEventListener(EVT, on);
    window.addEventListener(DROP, off);
    return () => { window.removeEventListener(EVT, on); window.removeEventListener(DROP, off); };
  }, []);

  // 로그아웃 · 다른 계정 · 같은 계정 재로그인 — 앞 세션의 알림과 「되돌리기」 를 남기지 않는다(공용 PC 에서 앞 사람의 관리 작업이 보였다)
  useEffect(() => {
    let cur = toastSession();
    return onAuthChange(() => {
      const k = toastSession();
      if (k === cur) return;
      cur = k;
      setItems((xs) => xs.filter((x) => x.sid === k));
    });
  }, []);

  // 쌓인 높이 — 휴대폰에서 열린 담은 원단 패널이 이만큼 비켜 선다(m54). 높이 제한에 걸리거나 풀리면 묶음 크기가 바뀐다 — 그때 굴림도 다시 정한다
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const root = document.documentElement;
    const put = () => { root.style.setProperty('--toast-h', `${el.offsetHeight}px`); fitToaster(el); };
    put();
    const ro = new ResizeObserver(put);
    ro.observe(el);
    return () => { ro.disconnect(); root.style.removeProperty('--toast-h'); };
  }, []);

  /**
   * 등록된 행동 줄(useToastAvoid)과 겹치면 줄 윗변 위 --s3 까지 올린다(m68) — 올린 값은 --toast-lift(translate).
   * 「겹침」 은 올리기 전 자리로 잰다(올린 값을 되돌려 계산 — 올린 자리끼리 흔들리지 않게). 가로로 겹치지 않으면(PC 의 오른쪽 아래 토스트 ·
   * 왼쪽 기둥의 저장 줄) 그대로. 토스트가 있을 때만 잰다. 올린 자리는 모든 줄과 다시 잰다(toastLift).
   * 묶음이 높이 제한에 걸려 안에서 구르면 보이는 상자가 아니라 내용 전체 높이로 잰다(넘친 만큼 윗변을 위로) — 올림이 높이 제한에 따라 흔들리지 않게
   */
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    let raf = 0;
    const place = () => {
      raf = 0;
      const cur = parseFloat(el.style.getPropertyValue('--toast-lift')) || 0;
      let lift = 0;
      if (items.length && avoid.size) {
        const gap = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--s3')) || 12;
        const hidden = fitToaster(el) ? el.scrollHeight - el.clientHeight : 0;
        const r = el.getBoundingClientRect();
        // 화면 위 끝 + gap 아래에서만 — 회피 줄이 화면 위까지 이어져도 토스트가 화면 밖으로 나가지 않게(Codex 11차 새 2)
        lift = toastLift({ top: r.top + cur - hidden, bottom: r.bottom + cur, left: r.left, right: r.right },
                         [...avoid].map((a) => a.getBoundingClientRect()), gap, gap);
      }
      if (lift === cur) return;
      if (lift) el.style.setProperty('--toast-lift', `${lift}px`); else el.style.removeProperty('--toast-lift');
    };
    const ask = () => { if (!raf) raf = requestAnimationFrame(place); };
    place();
    window.addEventListener(AVOID_EVT, place);
    window.addEventListener('scroll', ask, { capture: true, passive: true });
    window.addEventListener('resize', ask);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener(AVOID_EVT, place);
      window.removeEventListener('scroll', ask, { capture: true });
      window.removeEventListener('resize', ask);
    };
  }, [items]);

  // 새 토스트가 왔는데 묶음이 높이 제한에 걸려 구르는 중이면 맨 아래(가장 새 토스트 — 회피 줄 · 화면 아래 쪽)를 보인다
  const newest = items.length ? items[items.length - 1].id : 0;
  useLayoutEffect(() => {
    const el = box.current;
    if (el && newest && fitToaster(el)) el.scrollTop = el.scrollHeight;
  }, [newest]);

  const drop = (id: number) => setItems((xs) => xs.filter((x) => x.id !== id));
  /** 행동은 만든 세션에서만 — 세션이 바뀌었으면 실행하지 않고 내린다 */
  const act = (m: ToastMsg) => {
    drop(m.id);
    if (m.sid !== toastSession()) return;
    m.action?.run();
  };

  // 알림 영역은 늘 붙어 있어야 화면 낭독기가 첫 토스트도 읽는다(비어 있을 때는 높이 0)
  return createPortal(
    <div ref={box} className={`toaster${tray ? ' on-tray' : ''}${runbar ? ' on-runbar' : ''}`} role="status" aria-live="polite">
      {items.map((m) => (
        <div key={m.id} className={`toast ${m.tone}`}>
          <span aria-hidden="true">
            <Icon name={m.tone === 'ok' ? 'check' : m.tone === 'error' ? 'error' : 'info'} size={14} />
          </span>
          <span className="toast-text">{m.text}</span>
          {m.action && (
            <button type="button" className="toast-act" onClick={() => act(m)}>
              {m.action.icon && <Icon name={m.action.icon} size={16} />} {m.action.label}
            </button>
          )}
          <button type="button" className="toast-x" aria-label="닫기" onClick={() => drop(m.id)}><Icon name="close" size={16} /></button>
        </div>
      ))}
    </div>,
    document.body,
  );
}
